import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, Home } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* keep trying */ }
      attempts.current += 1;
      if (attempts.current > 12) { setState("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-4 grain">
      <div className="relative z-10 max-w-md w-full glass rounded-3xl p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-mustard mx-auto animate-spin" />
            <h1 className="font-display font-bold text-2xl mt-6">Vérification du paiement…</h1>
            <p className="text-cream/60 mt-2 text-sm">Un instant, on confirme ton inscription.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-bright mx-auto" />
            <h1 className="font-display font-black text-2xl mt-6">Bienvenue dans la famille ! 🔥</h1>
            <p className="text-cream/70 mt-3">
              {info?.first_name ? `Merci ${info.first_name}, ton` : "Ton"} inscription est confirmée.
              Un email de confirmation vient de t'être envoyé.
            </p>
            {info?.formula_name && (
              <div className="mt-5 rounded-xl bg-ink border border-white/10 p-4 text-sm">
                <div className="text-cream/50">Formule</div>
                <div className="font-sub font-semibold text-mustard">{info.formula_name}</div>
              </div>
            )}
            <button data-testid="success-home-btn" onClick={() => navigate("/")} className="btn-mustard rounded-full px-6 py-3 mt-7 inline-flex items-center gap-2">
              <Home className="w-4 h-4" /> Retour au site
            </button>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="w-16 h-16 text-destructive mx-auto" />
            <h1 className="font-display font-bold text-2xl mt-6">Paiement non confirmé</h1>
            <p className="text-cream/60 mt-3 text-sm">Le paiement n'a pas pu être vérifié. Si tu as été débité, contacte-nous.</p>
            <button onClick={() => navigate("/")} className="rounded-full px-6 py-3 mt-7 border border-white/20 text-cream hover:border-mustard transition">Retour au site</button>
          </>
        )}
      </div>
    </div>
  );
}
